import { useTranslations } from 'next-intl';
import { Avatar, Icon } from '@/components/site/ui';
import { cn, formatMoney } from '@/lib/utils';
import type { ClinicBookingContent, ClinicBookingService } from '@/lib/clinic-booking';

/** Who the booking is with, the chosen service (once chosen) and where it happens. */
export function BookingProfessionalSummary({
  professional,
  service,
  compact = false,
}: {
  professional: ClinicBookingContent;
  /** The chosen service; omitted until the person picks one. */
  service?: ClinicBookingService;
  compact?: boolean;
}) {
  const t = useTranslations('clinicBooking');
  const details = (
    <div className="space-y-4">
      <p className="flex items-start gap-3 text-sm text-muted-foreground">
        <Icon name="location-01" size={18} className="mt-0.5 shrink-0 text-medlux" aria-hidden />
        <span className="sr-only">{t('address')}: </span>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(professional.address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="whitespace-pre-wrap decoration-dotted decoration-2 underline-offset-4 hover:text-foreground hover:underline focus-visible:underline"
        >
          {professional.address}
        </a>
      </p>
    </div>
  );
  return (
    <div className={cn('space-y-6', compact && 'space-y-4 lg:space-y-6')}>
      <div className="flex items-center gap-4">
        <Avatar src={professional.photoUrl} name={professional.name} size={72} className={cn(compact && 'max-lg:size-12! max-lg:text-base!')} />
        <div className="min-w-0 space-y-1">
          <h2 className={cn('text-xl font-semibold tracking-tight', compact && 'max-lg:text-lg max-lg:leading-snug')}>{professional.name}</h2>
          <p className="text-sm text-muted-foreground">{professional.registration}</p>
        </div>
      </div>
      {service && (
      <div
        className={cn(
          'space-y-2 border-t border-border pt-5',
          compact &&
            'flex flex-wrap items-center justify-between gap-2 space-y-0 pt-4 lg:block lg:space-y-2 lg:pt-5',
        )}
      >
        <h3 className={cn('font-semibold', compact && 'hidden lg:block')}>
          {service.name}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('inPerson')} · {t('duration', { minutes: service.durationMinutes })}
        </p>
        <p className="text-lg font-semibold">
          {formatMoney(service.priceCents, 'BRL', 'pt-BR')}
        </p>
      </div>
      )}
      <div className={cn('border-t border-border pt-5', compact && 'max-lg:pt-4')}>{details}</div>
    </div>
  );
}
